import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Calculator,
  Sparkles,
  Plane,
  CreditCard,
  ArrowRight,
  Globe2,
  TrendingUp,
  Wallet,
  BadgeDollarSign,
} from "lucide-react";

const features = [
  {
    icon: Calculator,
    title: "Reward Value Calculator",
    slug: "reward-value-calculator",
    description:
      "Estimate the potential travel value of your airline miles and reward points across different redemption options.",
    details:
      "This tool helps you calculate the real-world value of your reward points based on airline partners, cash value, and travel class upgrades.",
  },
  {
    icon: Sparkles,
    title: "Smart Redemption Suggestions",
    slug: "smart-redemption-suggestions",
    description:
      "Discover estimated redemption opportunities based on your points balance, travel preferences, and destination.",
    details:
      "Get AI-style suggestions for the best way to redeem your points based on current airline deals and transfer partners.",
  },
  {
    icon: Plane,
    title: "Premium Cabin Opportunities",
    slug: "premium-cabin-opportunities",
    description:
      "Explore potential business and first-class travel opportunities using reward points and transfer partners.",
    details:
      "Find hidden premium cabin deals where your points unlock luxury travel experiences at the lowest cost.",
  },
  {
    icon: CreditCard,
    title: "Multi-Card Support",
    slug: "multi-card-support",
    description:
      "Compare redemption possibilities across major US reward programs including travel and airline loyalty cards.",
    details:
      "Connect multiple credit cards and compare which one gives maximum reward value per booking.",
  },
  {
    icon: Globe2,
    title: "Destination Reward Analysis",
    slug: "destination-reward-analysis",
    description:
      "Analyze estimated reward value between departure cities and international travel destinations.",
    details:
      "See which destinations give the highest value per point depending on season and demand.",
  },
  {
    icon: TrendingUp,
    title: "Transfer Partner Insights",
    slug: "transfer-partner-insights",
    description:
      "Explore potential airline transfer opportunities and compare estimated travel reward value.",
    details:
      "Understand which airline partners give the best transfer ratio for your credit card points.",
  },
  {
    icon: Wallet,
    title: "Flexible Reward Comparisons",
    slug: "flexible-reward-comparisons",
    description:
      "Compare cashback, travel rewards, gift cards, and airline transfer redemption opportunities.",
    details:
      "Evaluate all redemption options side-by-side to maximize your financial benefit.",
  },
  {
    icon: BadgeDollarSign,
    title: "Estimated Point Value Tracking",
    slug: "estimated-point-value-tracking",
    description:
      "Understand estimated cents-per-point value across multiple redemption categories and travel programs.",
    details:
      "Track how your point value changes over time and optimize redemption timing.",
  },
];

const FeatureDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const feature = features.find((f) => f.slug === slug);

  if (!feature) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <h1 className="text-2xl font-bold">Feature Not Found</h1>
      </div>
    );
  }

  const Icon = feature.icon;

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-20">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="mb-10 text-[#D4AF37] font-bold"
        >
          ← Back
        </button>

        <div className="bg-white p-10 rounded-3xl shadow-lg">
          <div className="w-16 h-16 bg-yellow-100 flex items-center justify-center rounded-2xl mb-6">
            <Icon size={30} className="text-[#D4AF37]" />
          </div>

          <h1 className="text-4xl font-black text-slate-900">
            {feature.title}
          </h1>

          <p className="mt-6 text-slate-600 text-lg leading-relaxed">
            {feature.description}
          </p>

          <div className="mt-10 p-6 bg-slate-50 rounded-2xl border">
            <h3 className="text-xl font-bold mb-2">Detailed Insights</h3>

            <p className="text-slate-700">{feature.details}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureDetails;
